import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatAmount, shortAddress } from "@/lib/api";
import { Shield, Ban, Users, TrendingUp, DollarSign, Dices, Flag, ArrowUpFromLine, ArrowDownToLine } from "lucide-react";
import { StatusChip } from "@/pages/Withdraw";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [race, setRace] = useState(null);
  const [pool, setPool] = useState("");

  const load = async () => {
    const [u, s, w, r, um] = await Promise.all([
      api.get("/admin/users?limit=200"), api.get("/admin/stats"), api.get("/admin/withdrawals?limit=100"), api.get("/race"), api.get("/admin/deposits/unmatched"),
    ]);
    setUsers(u.data);
    setStats(s.data);
    setWithdrawals(w.data);
    setRace(r.data);
    setUnmatched(um.data);
    setPool(String(r.data.pool));
  };

  const assignUnmatched = async (row) => {
    const who = window.prompt(`Assign ${row.amount} ${row.currency} (tx ${row.txid.slice(0, 10)}…) to user — enter email, username or id:`);
    if (!who) return;
    try {
      const { data } = await api.post(`/admin/deposits/unmatched/${row.id}/assign`, { user_id: who.trim() });
      toast.success(`Credited to ${data.username}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleBan = async (uid, banned) => {
    try {
      await api.post(`/admin/users/${uid}/${banned ? "unban" : "ban"}`);
      toast.success(banned ? "User unbanned" : "User banned");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const credit = async (uid) => {
    const raw = window.prompt("Credit amount of FMI to add (negative to debit):", "100");
    if (!raw) return;
    const n = Number(raw);
    if (!Number.isFinite(n) || n === 0) return;
    try {
      await api.post(`/admin/users/${uid}/adjust`, { currency: "FMI", amount: n });
      toast.success(`Adjusted ${n} FMI`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const manualDeposit = async (uid) => {
    const raw = window.prompt("Manual deposit credit (applies first-deposit bonus), e.g. 0.01 ETH, 0.001 BTC, 50 USDT or 1000 FMI:", "0.01 ETH");
    if (!raw) return;
    const [amt, cur] = raw.trim().split(/\s+/);
    const n = Number(amt);
    if (!Number.isFinite(n) || n <= 0 || !cur) return toast.error("Format: <amount> <BTC|ETH>");
    try {
      const { data } = await api.post(`/admin/users/${uid}/deposit`, { currency: cur.toUpperCase(), amount: n });
      toast.success(`Credited ${n} ${cur.toUpperCase()}${data.bonus ? ` · bonus ${formatAmount(data.bonus.amount, "FMI")} FMI` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const actWithdrawal = async (w, action) => {
    let body = {};
    if (action === "paid") {
      const txid = window.prompt("Transaction hash of the payout:");
      if (!txid) return;
      body = { txid };
    }
    if (action === "reject") body = { reason: window.prompt("Reason (optional):") || null };
    try {
      await api.post(`/admin/withdrawals/${w.id}/${action}`, body);
      toast.success(`Withdrawal ${action === "paid" ? "marked paid" : action === "reject" ? "rejected" : action + "d"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const savePool = async () => {
    try {
      await api.put("/admin/race", { pool: Number(pool) });
      toast.success("Race pool updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const runJob = async (path, label) => {
    try {
      const { data } = await api.post(path);
      const paid = data.total_paid ?? data.total;
      toast.success(`${label}: ${data.status}${paid != null ? ` · ${formatAmount(paid, "FMI")} FMI` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const openWithdrawals = withdrawals.filter((w) => w.status === "pending" || w.status === "approved");


  return (
    <div className="flex flex-col gap-6 fade-up">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter flex items-center gap-3">
          <Shield className="text-amber-400" size={32} /> Admin Control
        </h1>
        <div className="text-zinc-500 text-sm mt-1">Operational intel · risk · ledgers</div>
      </div>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Users} label="Users" value={stats?.total_users} />
        <Kpi icon={Dices} label="Bets" value={stats?.total_bets} />
        <Kpi icon={TrendingUp} label="Wagered" value={formatAmount(stats?.total_wagered || 0, "FMI")} />
        <Kpi icon={DollarSign} label="House P/L" value={formatAmount(stats?.house_profit || 0, "FMI")} accent={(stats?.house_profit || 0) >= 0 ? "#00E676" : "#FF3366"} />
      </section>

      <section>
        <h2 className="font-display text-xl font-black tracking-tighter mb-3">By Game</h2>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {(stats?.per_game || []).map((g) => (
            <div key={g.game} className="panel p-4" data-testid={`admin-game-${g.game}`}>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">{g.game}</div>
              <div className="font-mono-num text-xl font-bold mt-1">{g.bets}</div>
              <div className="text-xs text-zinc-500 mt-2">Vol {formatAmount(g.volume, "FMI")}</div>
              <div className={`text-xs font-mono-num ${g.house_profit >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                House {g.house_profit >= 0 ? "+" : ""}{formatAmount(g.house_profit, "FMI")}
              </div>
            </div>
          ))}
          {(!stats?.per_game || stats.per_game.length === 0) && (
            <div className="col-span-full text-zinc-600 text-sm">No game activity yet.</div>
          )}
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-black tracking-tighter mb-3 flex items-center gap-2">
          <ArrowUpFromLine size={18} className="text-amber-400" /> Withdrawals
          {openWithdrawals.length > 0 && <span className="chip text-amber-400 border-amber-500/30" data-testid="admin-withdrawals-open">{openWithdrawals.length} open</span>}
        </h2>
        <div className="panel overflow-hidden overflow-x-auto">
          <table className="data-table" data-testid="admin-withdrawals-table">
            <thead>
              <tr><th>Player</th><th>Amount</th><th>Address</th><th>Status</th><th>Requested</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {withdrawals.length === 0 ? (
                <tr><td colSpan={6} className="text-zinc-600 text-center py-8">No withdrawal requests.</td></tr>
              ) : withdrawals.map((w) => (
                <tr key={w.id} data-testid={`admin-withdrawal-${w.id}`}>
                  <td className="font-semibold">{w.username}</td>
                  <td className="font-mono-num">{formatAmount(w.amount, w.currency)} {w.currency}</td>
                  <td className="font-mono-num text-xs text-zinc-400 max-w-[200px] truncate" title={w.address}>{w.address}</td>
                  <td><StatusChip status={w.status} />{w.txid && <div className="text-[10px] text-zinc-500 font-mono-num truncate max-w-[140px]">{w.txid}</div>}</td>
                  <td className="text-xs text-zinc-500 font-mono-num">{new Date(w.created_at).toLocaleString()}</td>
                  <td>
                    {(w.status === "pending" || w.status === "approved") && (
                      <div className="flex gap-2">
                        {w.status === "pending" && (
                          <button onClick={() => actWithdrawal(w, "approve")} className="btn-ghost rounded-md text-xs px-3 py-1.5 text-cyan-300" data-testid={`admin-wd-approve-${w.id}`}>Approve</button>
                        )}
                        <button onClick={() => actWithdrawal(w, "paid")} className="btn-ghost rounded-md text-xs px-3 py-1.5 text-emerald-400" data-testid={`admin-wd-paid-${w.id}`}>Mark paid</button>
                        <button onClick={() => actWithdrawal(w, "reject")} className="btn-ghost rounded-md text-xs px-3 py-1.5 text-rose-400" data-testid={`admin-wd-reject-${w.id}`}>Reject</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-black tracking-tighter mb-3 flex items-center gap-2">
          <ArrowDownToLine size={18} className="text-amber-400" /> Unmatched mainnet deposits
          {unmatched.filter((u) => u.status === "unmatched").length > 0 && <span className="chip text-rose-400 border-rose-500/30" data-testid="admin-unmatched-count">{unmatched.filter((u) => u.status === "unmatched").length} to review</span>}
        </h2>
        <div className="panel overflow-hidden overflow-x-auto">
          <table className="data-table" data-testid="admin-unmatched-table">
            <thead><tr><th>Asset</th><th>Amount</th><th>Tx</th><th>Seen</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>
              {unmatched.length === 0 ? (
                <tr><td colSpan={6} className="text-zinc-600 text-center py-8">Every incoming mainnet transfer matched a player's deposit instructions.</td></tr>
              ) : unmatched.map((u) => (
                <tr key={u.id} data-testid={`admin-unmatched-${u.id}`}>
                  <td className="font-semibold">{u.currency}</td>
                  <td className="font-mono-num">{u.amount}</td>
                  <td className="font-mono-num text-xs text-zinc-400"><a href={u.explorer_url} target="_blank" rel="noreferrer" className="hover:text-cyan-300">{u.txid.slice(0, 18)}…</a></td>
                  <td className="text-xs text-zinc-500 font-mono-num">{new Date(u.seen_at).toLocaleString()}</td>
                  <td><span className={`chip text-[10px] uppercase ${u.status === "assigned" ? "text-emerald-400 border-emerald-500/30" : "text-rose-400 border-rose-500/30"}`}>{u.status}</span>{u.assigned_username && <div className="text-[10px] text-zinc-500 mt-1">→ {u.assigned_username}</div>}</td>
                  <td>{u.status === "unmatched" && <button onClick={() => assignUnmatched(u)} className="btn-ghost rounded-md text-xs px-3 py-1.5 text-cyan-300" data-testid={`admin-unmatched-assign-${u.id}`}>Assign to player</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-black tracking-tighter mb-3 flex items-center gap-2"><Flag size={18} className="text-amber-400" /> Weekly race & payouts</h2>
        <div className="panel p-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5 items-end">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Prize pool (FMI) · split 30/20/12/10/8/6/5/4/3/2</div>
            <div className="flex gap-2">
              <input type="number" min="0" value={pool} onChange={(e) => setPool(e.target.value)} className="panel px-4 py-2.5 outline-none font-mono-num w-48" style={{ background: "#0F0F11" }} data-testid="admin-race-pool-input" />
              <button onClick={savePool} className="btn-cyan rounded-lg px-4 py-2.5 text-sm" data-testid="admin-race-pool-save">Save</button>
            </div>
            <div className="text-xs text-zinc-500 mt-2">Current week leader: <span className="text-white">{race?.standings?.[0]?.username || "—"}</span> · {race?.standings?.length || 0} players in the race</div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => runJob("/admin/race/run", "Race")} className="btn-ghost rounded-lg px-4 py-2.5 text-sm" data-testid="admin-race-run">Pay last week's race</button>
            <button onClick={() => runJob("/admin/rakeback/run", "Rakeback")} className="btn-ghost rounded-lg px-4 py-2.5 text-sm" data-testid="admin-rakeback-run">Pay last week's rakeback</button>
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-black tracking-tighter mb-3">Users</h2>
        <div className="panel overflow-hidden overflow-x-auto">
          <table className="data-table" data-testid="admin-users-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Role</th>
                <th>FMI</th>
                <th>Created</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div className="font-semibold">{u.username}</div>
                    <div className="text-xs text-zinc-500 font-mono-num">
                      {u.email || (u.wallet ? shortAddress(u.wallet) : "—")}
                    </div>
                  </td>
                  <td>
                    <span className={`chip ${u.role === "admin" ? "" : ""}`} style={u.role === "admin" ? { color: "#FFC107", borderColor: "rgba(255,193,7,0.3)" } : {}}>
                      {u.role}
                    </span>
                  </td>
                  <td className="font-mono-num">{formatAmount(u.balances?.FMI || 0, "FMI")}</td>
                  <td className="text-xs text-zinc-500 font-mono-num">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td>
                    {u.banned ? (
                      <span className="text-rose-400 text-xs uppercase tracking-widest">Banned</span>
                    ) : (
                      <span className="text-emerald-400 text-xs uppercase tracking-widest">Active</span>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        onClick={() => credit(u.id)}
                        className="btn-ghost rounded-md text-xs px-3 py-1.5"
                        data-testid={`admin-credit-${u.id}`}
                      >
                        Credit
                      </button>
                      <button
                        onClick={() => manualDeposit(u.id)}
                        className="btn-ghost rounded-md text-xs px-3 py-1.5"
                        data-testid={`admin-deposit-${u.id}`}
                      >
                        Deposit
                      </button>
                      <button
                        onClick={() => toggleBan(u.id, u.banned)}
                        className="btn-ghost rounded-md text-xs px-3 py-1.5 flex items-center gap-1"
                        data-testid={`admin-ban-${u.id}`}
                      >
                        <Ban size={12} /> {u.banned ? "Unban" : "Ban"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent = "#F4F4F5" }) {
  return (
    <div className="panel p-5 flex items-start justify-between" data-testid={`admin-kpi-${label.toLowerCase()}`}>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        <div className="font-mono-num text-2xl font-bold mt-2" style={{ color: accent }}>
          {value ?? "—"}
        </div>
      </div>
      <Icon size={22} className="text-zinc-700" />
    </div>
  );
}
